import { css, html, LitElement, nothing, PropertyValues } from "lit"
import { customElement, property, query, state } from "lit/decorators.js"
import { getAlternateLinks, UmbracoPage } from "../ContentLoader"
import { unsafeSVG } from "lit/directives/unsafe-svg.js"
import { UmbNavItem } from "../api/UmbNavItem"
import { unsafeHTML } from "lit/directives/unsafe-html.js"
import { RichTextStyles } from "../Shared/RichText"
import { Flags } from "../Shared/Flags/Flag"
import { sortBy } from "../SortBy"
import { RichTextModel } from "../api/ContentDeliveryApi.schemas"

@customElement('lindberg-footer')
export class Footer extends LitElement {

    @query('footer')
    footer!: HTMLElement

    mediumBreak?: number
    smallBreak?: number

    @state()
    showLanguages = false


    static styles = [RichTextStyles, css`
        :host {
            display: grid;
            font-size: var(--font-size-body-md);
            color: var(--color-navigation-primary);
            background-color: var(--color-primary-sand-300);

            a, button {
                all: unset;
                cursor: pointer;
                color: inherit;
                font: inherit;
                text-decoration: none;
                &:focus-visible {
                    outline: 2px solid var(--color-secondary-blue);
                    outline-offset: 2px;
                }
            }

            footer {
                display: grid;
                grid-template-columns: auto auto;
                justify-content: space-between;
                padding: var(--space-900) 0 var(--space-700) 0;
                width: 100%;
                white-space:nowrap;
                gap: var(--space-300);               

                > nav {
                    display: flex;
                    gap: var(--space-100);

			        justify-content: center;
                    position: relative;


                    &:has(lindberg-icon){
                        gap: 0;
                    }
                }

                a, button{
                    padding-inline: var(--space-100);
                    padding-block: var(--space-100);
                }

                :host(:not(.medium):not(.small)) & {
                    max-width: 1160px;
                    place-self: center;
                    & > nav:nth-of-type(odd) {
                        justify-content: start;
			        }

                    & > nav:nth-of-type(even) {
                        justify-content: end;
			        }
                    a, button {
                        padding-inline: var(--space-200);
                    }

                    &::before {
                        content: "";
                        grid-column: 1 / -1;   /* span all columns */
                        grid-row: 2;           /* the middle row */
                        align-self: center;    /* vertical center of that row */
                        height: 1px;
                        opacity: 0.25;
                        margin-inline: var(--space-200);
                        background: var(--color-primary-gray-500, #5E5C5B);
                        pointer-events: none;
                    }
                }

                :host(.medium) & {
                    width: auto;
                    grid-template-columns: auto;
                    align-items: center;
                    text-align: center;
		            margin-inline: auto;

                    hr {
                        margin-block: var(--space-100);
                    }
                }

                :host(.small) & {
                    width: auto;
                    grid-template-columns: auto;
                    align-items: center;
                    text-align: center;
		            margin-inline: auto;

                    nav:not(:has(lindberg-icon)) {
                        flex-direction: column;
                    }
                }
                svg {
                    border-radius: 50%;
                    width: 24px;
                    height: 24px;
                    vertical-align: middle;
                    margin-right: var(--space-200);
                }
            }
            div.copyright, div.copyright p {
                text-align: center;
                padding: var(--space-150) var(--space-300);
                background-color: var(--color-primary-sand-400);
                font-size: var(--font-size-body-xs);
            }
        }
        nav.sub { 
            display: flex;
            flex-direction: column;
            text-align: left;
            .selected {
                font-weight: var(--font-weight-medium);
            }
        }
        :host(:not(.medium):not(.small)) nav.sub {
            position: absolute;
            background: var(--color-primary-sand-200);
            bottom: 0;
            z-index: 1000;
        }
        :host(:is(.small, .medium)) button:has(+ nav.sub) {
            display: none;
        }
        

    `]

    @property({ attribute: false })
    currentPage!: UmbracoPage

    @property({ attribute: false })
    root!: UmbracoPage

    render() {
        const page = this.currentPage || this.root
        const currentCulture = Object.keys(page.cultures || {}).find(x => page.route.path == page.cultures[x].path) ?? "en"
	
        const alternateLinks = getAlternateLinks(page)

        // Drop placeholder links to "#", unless it's the language picker
        const items = (this.root.properties.footer as UmbNavItem[]).filter(x => x.url != '#' || x.itemType == 'language')

        const result = items.reduce((acc, item) => {
            if (item.itemType === "divider") {
                acc.push([]);
            } else {
                acc[acc.length - 1].push(item);
            }
            return acc;
        }, [[]] as UmbNavItem[][]);

        const [group1, group2, group3, group4] = result;

        return html`
            <footer>
                ${result.map(group => html`
                    <nav>
                        ${group.map(x => x.itemType == "language" ? html`
                            <button class="language"
                                aria-expanded=${this.showLanguages ? 'true' : 'false'}
                                aria-controls="footer-languages"
                                @click=${() => this.showLanguages = !this.showLanguages}
                                @keydown=${this.onLanguageKeydown}>
                                <span aria-hidden="true">${unsafeSVG(Flags[currentCulture.toUpperCase()])}</span>
                                ${new Intl.DisplayNames([currentCulture], { type: 'language' }).of(currentCulture)?.toLowerCase()}
                            </button>
                            ${this.showLanguages ? html`<nav class="sub" id="footer-languages"
                                aria-label=${new Intl.DisplayNames([currentCulture], { type: 'language' }).of(currentCulture) ?? currentCulture}
                                @keydown=${this.onLanguageKeydown}>
                                ${Object.entries(alternateLinks).map(([lang, url]) => html`
                                    <a href=${url} hreflang=${lang} lang=${lang}
                                        class="${lang === currentCulture ? 'selected' : ''}"
                                        aria-current=${lang === currentCulture ? 'page' : nothing}>
                                        <span aria-hidden="true">${unsafeSVG(Flags[lang.toUpperCase()])}</span>
                                        ${new Intl.DisplayNames([lang], { type: 'language' }).of(lang)?.toLowerCase()}
                                    </a>
                                `)}
                            </nav>` : nothing}` :
                            html`<a href=${x.url} target=${x.target || nothing}
                                aria-label=${x.image ? (x.title || x.name) : nothing}>
                            ${x.image ? html`<lindberg-icon aria-hidden="true">${unsafeSVG(x.image)}</lindberg-icon>` : x.title || x.name}
                        </a>`)}
                    </nav>
                `)}
            </footer>
            ${(this.root.properties?.copyright as RichTextModel)?.markup ? html`<div class="copyright">${unsafeHTML((this.root.properties?.copyright as RichTextModel).markup)}</div>` : nothing}    
        `
    }

    connectedCallback() {
        super.connectedCallback()
        document.addEventListener('click', this.onDocumentClick)
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        document.removeEventListener('click', this.onDocumentClick)
    }

    private onDocumentClick = (e: MouseEvent) => {
        if (!this.showLanguages) return
        const inside = e.composedPath().some(el =>
            el instanceof HTMLElement && (el.matches('button.language') || el.matches('nav.sub')))
        if (!inside) this.showLanguages = false
    }

    private onLanguageKeydown = (e: KeyboardEvent) => {
        if (e.key !== 'Escape' || !this.showLanguages) return
        this.showLanguages = false
        this.renderRoot.querySelector<HTMLButtonElement>('button.language')?.focus()
    }

    protected firstUpdated() {
        window.addEventListener('resize', this.updated.bind(this))

    }

    protected updated() {
        const width = this.clientWidth

        // Upgrade back to default
        if (this.smallBreak && width >= this.smallBreak) {
            this.classList.remove('small')
            this.smallBreak = undefined
        }

        if (this.mediumBreak && width >= this.mediumBreak) {
            this.classList.remove('medium')
            this.mediumBreak = undefined
        }

        // If already small, nothing else to do
        if (this.classList.contains('small')) return

        // Escalate: default -> medium (and maybe immediately to small)
        if (!this.classList.contains('medium')) {
            if (this.footer.scrollWidth > width) {
                this.mediumBreak = this.footer.scrollWidth
                this.classList.add('medium')

                // Re-check after medium styles apply (same frame, but updated() already ran with them)
                // So do a second check right away using the current DOM state:
                if (this.footer.scrollWidth > width) {
                    this.smallBreak = this.footer.scrollWidth
                    this.classList.remove('medium')
                    this.classList.add('small')
                }
            }

            return
        }

        // Escalate: medium -> small
        if (this.footer.scrollWidth > width) {
            this.smallBreak = this.footer.scrollWidth
            this.classList.remove('medium')
            this.classList.add('small')
        }
    }
}
